export class ApiClient {
    private static _accessTokenPromise: Promise<string> | null = null;
    private static _accessTokenRequested: number = 0;

    static requestNewTokenAfter = 1000 * 60 * 5; // 5 minutes

    constructor(private readonly baseUrl = '/api/v1') {
    }

    async get(url: string, withoutAuth = false) {
        return await this.send(url, await this.getFetchOptions('GET', null, withoutAuth));
    }

    async post(url: string, data: any) {
        return await this.send(url, await this.getFetchOptions('POST', data));
    }

    async patch(url: string, data: any) {
        return await this.send(url, await this.getFetchOptions('PATCH', data));
    }

    async put(url: string, data: any) {
        return await this.send(url, await this.getFetchOptions('PUT', data));
    }

    async delete(url: string) {
        const options = await this.getFetchOptions('DELETE');
        return await this.send(url, options);
    }

    async send(url: string, options: RequestInit) {
        const fullUrl = url.startsWith('http') ? url : this.baseUrl.replace(/\/$/, '') + '/' + url.replace(/^\//, '');
        const response = await fetch(fullUrl, options);
        const body = await response.text();
        const content = body ? JSON.parse(body) : null;

        if (response.ok) {
            return content;
        } else if (response.status == 404) {
            return null;
        } else {
            throw new ApiError(response.status, content);
        }
    }

    private async getFetchOptions(method: string, body?: any, withoutAuth = false): Promise<RequestInit> {
        const options = {
            method,
            body: null as BodyInit | null,
            headers: new Headers({
                Accept: 'application/json',
            })
        };

        const accessToken = withoutAuth ? null : await this.getAccessToken();
        if (accessToken) {
            options.headers.set('Authorization', `Bearer-MS ${accessToken}`);
        }

        if (body) {
            options.body = JSON.stringify(body);
            options.headers.set('Content-Type', 'application/json');
        }

        return options;
    }

    private async getAccessToken() {
        if (!ApiClient._accessTokenPromise || ApiClient._accessTokenRequested + ApiClient.requestNewTokenAfter < Date.now()) {
            ApiClient._accessTokenRequested = Date.now();
            ApiClient._accessTokenPromise = (async () => {
                await Office.onReady();
                return await Office.auth?.getAccessToken({
                    allowConsentPrompt: true,
                    allowSignInPrompt: true,
                });
            })();
        }

        return await ApiClient._accessTokenPromise;
    }

    public static encodeArray(array: any[], key: string) {
        return array.map(value => `${key}=${encodeURIComponent(value)}`).join('&');
    }
}

export class ApiError extends Error {
    public error: string

    constructor(
        public status: number,
        public content: any) {
        super(content?.message || 'API error');
        this.error = content?.summary?.replace(/\[\w+\] Error from Rest API:/, '') || content?.error_description || this.message || '';
        this.error = this.error.trim();
        if (this.error.startsWith('{')) {
            try {
                const model = JSON.parse(this.error);
                if (model.Issues && Array.isArray(model.Issues)) {
                    this.error = model.Issues.join(', \n');
                } else if (model.Message) {
                    this.error = model.Message;
                }
            } catch { }
        }
    }
}